import {
  type GetWorkspaceRolePermissionsQuery,
  getWorkspaceRolePermissionsQuery,
} from '@affine/graphql';
import {
  PERMISSION_SNAPSHOT_MAX_REQUEST_RESOURCES,
  permissionSnapshotPartitionIndex,
  type WorkspacePermissionGraphAdvancedPayload,
} from '@streamlain/permission-snapshot-contract';
import type { LiveData } from '@toeverything/infra';
import { Store } from '@toeverything/infra';

import { defaultRequestCoordinator } from '../../cloud/services/request-coordinator';
import { defaultWorkspaceApiClient } from '../../cloud/services/workspace-api-client';
import type { WorkspaceServerService } from '../../cloud/services/workspace-server';
import type { AccessibleDocCacheStore } from '../../doc/stores/accessible-doc-cache';
import { getLocalAuthorityClientInstallId } from '../../local-authority';
import type { WorkspaceService } from '../../workspace';
import {
  type PermissionChangeEvent,
  type PermissionSnapshotBundle,
  type PermissionSnapshotResource,
  PermissionSnapshotStore,
} from './permission-snapshot';

export type WorkspacePermissionActions = keyof Omit<
  GetWorkspaceRolePermissionsQuery['workspaceRolePermissions']['permissions'],
  '__typename'
>;

export type DocPermissionActions =
  | 'Doc_Comments_Create'
  | 'Doc_Comments_Delete'
  | 'Doc_Comments_Read'
  | 'Doc_Comments_Resolve'
  | 'Doc_Copy'
  | 'Doc_Delete'
  | 'Doc_Duplicate'
  | 'Doc_Properties_Read'
  | 'Doc_Properties_Update'
  | 'Doc_Publish'
  | 'Doc_Read'
  | 'Doc_Restore'
  | 'Doc_TransferOwner'
  | 'Doc_Trash'
  | 'Doc_Update'
  | 'Doc_Users_Manage'
  | 'Doc_Users_Read';

export function getNoDocPermissions(): Record<DocPermissionActions, boolean> {
  return {
    Doc_Read: false,
    Doc_Update: false,
    Doc_Delete: false,
    Doc_Copy: false,
    Doc_Duplicate: false,
    Doc_Publish: false,
    Doc_Restore: false,
    Doc_Properties_Read: false,
    Doc_Properties_Update: false,
    Doc_Users_Manage: false,
    Doc_Users_Read: false,
    Doc_Trash: false,
    Doc_TransferOwner: false,
    Doc_Comments_Create: false,
    Doc_Comments_Delete: false,
    Doc_Comments_Read: false,
    Doc_Comments_Resolve: false,
  } as Record<DocPermissionActions, boolean>;
}

const DOC_PERMISSION_ACTIONS = Object.keys(getNoDocPermissions());
const MAX_PERMISSION_SNAPSHOT_FRESH_ATTEMPTS = 3;

export class GuardStore extends Store {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly permissionSnapshotStore: PermissionSnapshotStore,
    private readonly accessibleDocCacheStore: AccessibleDocCacheStore
  ) {
    super();
  }

  async getWorkspacePermissions(): Promise<
    Record<WorkspacePermissionActions, boolean>
  > {
    if (!this.workspaceServerService.server) {
      throw new Error('No server');
    }
    const data = await this.workspaceServerService.server.gql({
      query: getWorkspaceRolePermissionsQuery,
      variables: {
        id: this.workspaceService.workspace.id,
      },
    });
    return data.workspaceRolePermissions.permissions;
  }

  async getDocPermissions(
    docId: string
  ): Promise<Record<DocPermissionActions, boolean>> {
    await this.permissionSnapshotStore.hydrateLocal();
    const cached = this.getCachedDocPermissions(docId);
    if (cached) {
      return cached;
    }
    const permissions = await this.getDocPermissionsBatch([docId]);
    return permissions[docId] ?? getNoDocPermissions();
  }

  async hydrateLocalPermissionSnapshot(): Promise<void> {
    await this.permissionSnapshotStore.hydrateLocal();
  }

  getCachedDocPermissions(
    docId: string
  ): Record<DocPermissionActions, boolean> | null {
    return this.normalizeDocPermissions(
      this.permissionSnapshotStore.getDocPermissions(docId)
    );
  }

  watchCachedDocPermissions(
    docId: string
  ): LiveData<Record<DocPermissionActions, boolean> | null> {
    return this.permissionSnapshotStore.grantsVersion$.map(() =>
      this.getCachedDocPermissions(docId)
    );
  }

  /** Doc ids whose offline grant expired without renewal. */
  watchExpiredDocIds(): LiveData<string[]> {
    return this.permissionSnapshotStore.expiredGrants$.map(expired =>
      [...expired]
        .filter(([, grant]) => grant.resourceType === 'doc')
        .map(([key]) => key.slice('doc:'.length))
    );
  }

  /** Marks the grants an event names as stale, once the vault is loaded. */
  async applyPermissionEvent(
    event: PermissionChangeEvent
  ): Promise<{ staleDocIds: string[]; full: boolean }> {
    await this.hydrateForEvent();
    return this.permissionSnapshotStore.applyPermissionEvent(event);
  }

  /** Confirms a counter value that does not affect this user. */
  async applyGraphAdvanced(
    event: WorkspacePermissionGraphAdvancedPayload
  ): Promise<{ full: boolean }> {
    await this.hydrateForEvent();
    return this.permissionSnapshotStore.applyGraphAdvanced(event);
  }

  /**
   * A vault that cannot be read must not swallow a permission event: its
   * stale marks are versioned, so they also hold for grants read later.
   */
  private async hydrateForEvent(): Promise<void> {
    try {
      await this.permissionSnapshotStore.hydrateLocal();
    } catch (error) {
      console.warn('permission vault read failed before an event', error);
    }
  }

  async getDocPermissionsBatch(
    docIds: string[],
    options: { force?: boolean } = {}
  ): Promise<Record<string, Record<DocPermissionActions, boolean>>> {
    await this.permissionSnapshotStore.hydrateLocal();
    const ids = Array.from(new Set(docIds.filter(Boolean)));
    if (ids.length === 0) {
      return {};
    }
    const server = this.workspaceServerService.server;
    if (!server) {
      return this.cachedPermissionsFor(ids);
    }
    const missingIds = options.force
      ? ids
      : ids.filter(id => !this.getCachedDocPermissions(id));
    if (missingIds.length === 0) {
      return this.cachedPermissionsFor(ids);
    }
    try {
      await defaultRequestCoordinator.executeBatch<
        string,
        Record<string, boolean>
      >({
        workspaceId: this.workspaceService.workspace.id,
        category: 'permissions',
        key: 'permission-snapshot:visible-docs',
        priority: 'visible',
        mode: 'background',
        items: missingIds,
        itemKey: id => id,
        maxBatchSize: PERMISSION_SNAPSHOT_MAX_REQUEST_RESOURCES,
        run: async (batch, signal) => {
          for (
            let attempt = 1;
            attempt <= MAX_PERMISSION_SNAPSHOT_FRESH_ATTEMPTS;
            attempt++
          ) {
            const response = await this.requestSnapshotBundle(
              server.baseUrl,
              batch.map(id => ({ id, type: 'doc' })),
              'visible',
              signal
            );
            await this.permissionSnapshotStore.applyOnlineSnapshots(response);

            // A permissions-changed event can mark grants stale while an
            // older signed response is in flight. The store correctly rejects
            // that response and keeps the grants stale. Fetch
            // again instead of silently converting the transient race into a
            // denied/readonly UI state. Every requested resource has a signed
            // grant, including explicit denied grants, so presence proves that
            // this response was accepted without weakening fail-closed rules.
            if (batch.every(id => this.getCachedDocPermissions(id) !== null)) {
              return Object.fromEntries(
                batch.map(id => [
                  id,
                  this.getCachedDocPermissions(id) ?? getNoDocPermissions(),
                ])
              );
            }
          }

          throw new Error(
            `Permission snapshot remained stale after ${MAX_PERMISSION_SNAPSHOT_FRESH_ATTEMPTS} attempts`
          );
        },
      });
    } catch {
      // The signed local snapshot remains the only offline authority. Unknown
      // docs fail closed instead of falling back to stale discoverability data.
    }
    return this.cachedPermissionsFor(ids);
  }

  /**
   * Fetches the complete grant set of one partition and replaces its base:
   * every resource the partition stores (docs and folders) plus every cached
   * page of that partition, sorted and sent chunk by chunk. An abort before
   * the last chunk keeps the stored base.
   */
  async refreshPartition(
    partitionIndex: number,
    signal?: AbortSignal
  ): Promise<void> {
    const server = this.workspaceServerService.server;
    if (!server) return;
    const resources = new Map<string, PermissionSnapshotResource>();
    for (const resource of await this.permissionSnapshotStore.getStoredPartitionResources(
      partitionIndex
    )) {
      resources.set(`${resource.type}:${resource.id}`, resource);
    }
    for (const doc of this.accessibleDocCacheStore.getCachedDocs()) {
      if (permissionSnapshotPartitionIndex('doc', doc.id) === partitionIndex) {
        resources.set(`doc:${doc.id}`, { id: doc.id, type: 'doc' });
      }
    }
    // Database system docs carry no own grant; their host doc does.
    const sorted = [...resources]
      .filter(([, resource]) => !resource.id.startsWith('db$'))
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
      .map(([, resource]) => resource);
    const chunkCount = Math.max(
      1,
      Math.ceil(sorted.length / PERMISSION_SNAPSHOT_MAX_REQUEST_RESOURCES)
    );
    const workspaceId = this.workspaceService.workspace.id;
    for (let chunkIndex = 0; chunkIndex < chunkCount; chunkIndex++) {
      const chunk = sorted.slice(
        chunkIndex * PERMISSION_SNAPSHOT_MAX_REQUEST_RESOURCES,
        (chunkIndex + 1) * PERMISSION_SNAPSHOT_MAX_REQUEST_RESOURCES
      );
      const bundle: PermissionSnapshotBundle =
        chunk.length === 0
          ? { snapshots: [] }
          : await defaultRequestCoordinator.execute({
              workspaceId,
              category: 'permissions',
              key: `permission-snapshot:partition:${partitionIndex}:${chunkIndex}`,
              priority: 'background',
              mode: 'background',
              signal,
              run: requestSignal =>
                this.requestSnapshotBundle(
                  server.baseUrl,
                  chunk,
                  'background',
                  requestSignal
                ),
            });
      signal?.throwIfAborted();
      await this.permissionSnapshotStore.applyOnlineSnapshots(bundle, {
        kind: 'base',
        partitionIndex,
        chunkIndex,
        chunkCount,
      });
    }
  }

  private requestSnapshotBundle(
    baseUrl: string,
    resources: readonly PermissionSnapshotResource[],
    priority: 'visible' | 'background',
    signal: AbortSignal
  ): Promise<PermissionSnapshotBundle> {
    const workspaceId = this.workspaceService.workspace.id;
    return defaultWorkspaceApiClient.requestJsonWithinCoordinator<PermissionSnapshotBundle>(
      {
        workspaceId,
        path: new URL(
          `/api/workspaces/${encodeURIComponent(workspaceId)}/permissions/snapshot`,
          baseUrl
        ).href,
        category: 'permissions',
        mode: 'background',
        priority,
        init: {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            clientInstallId: getLocalAuthorityClientInstallId(),
            visibilityMode: 'user_effective',
            resources,
          }),
        },
      },
      signal
    );
  }

  private cachedPermissionsFor(
    docIds: string[]
  ): Record<string, Record<DocPermissionActions, boolean>> {
    const result: Record<string, Record<DocPermissionActions, boolean>> = {};
    for (const docId of docIds) {
      result[docId] =
        this.getCachedDocPermissions(docId) ?? getNoDocPermissions();
    }
    return result;
  }

  private normalizeDocPermissions(
    source: Record<string, boolean> | null
  ): Record<DocPermissionActions, boolean> | null {
    if (!source) {
      return null;
    }
    const normalized = getNoDocPermissions();
    for (const action of DOC_PERMISSION_ACTIONS as DocPermissionActions[]) {
      normalized[action] = source[action] === true;
    }
    return normalized;
  }
}
